/** Conversational visa inquiry capture for the terminal assistant. */
import { todayISO, fmtAmt } from './utils.js';
import { prepareFlowSteps } from './terminal-flow-merge.js';
import { injectCustomSteps } from './terminal-flow-custom.js';
import { mergeCustomFieldsIntoExtra } from './terminal-flow-handlers.js';
import {
  capitalizeName,
  upperDoc,
  isYes,
  isNo,
  isSkip,
  parsePaxType,
} from './assistant-ticket-new.js';
import {
  parseSmartDate,
  validateSmartDate,
  DATE_HINT,
} from './assistant-datetime.js';

export function initVisaData(helpers = {}) {
  return {
    type: 'visa',
    name: '',
    phone: '',
    package_name: '',
    source: '',
    cost_amount: null,
    pax: { adt: 1, stu: 0, chd: 0, inf: 0 },
    pax_cost_rates: { adt: null, chd: null },
    pax_profit_rates: { adt: null, chd: null },
    pax_rates: { adt: null, chd: null, inf: null },
    india_evisa: null,
    passport_no: null,
    note: null,
    _packages: helpers.visaPackages || [],
    _costs: helpers.visaCosts || {},
  };
}

function isIndiaEvisa(pkg) {
  return /india\s*(e\s*)?visa/i.test(String(pkg || ''));
}

function parseLkrAmount(v) {
  const n = Number(String(v ?? '').trim().replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

function validateLkrAmount(v, { allowZero = false } = {}) {
  const n = parseLkrAmount(v);
  if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR.';
  if (!allowZero && n <= 0) return 'Enter amount in LKR.';
  return null;
}

export function buildVisaSteps(data, helpers = {}) {
  const { sources = [], matchSource = (v) => v, formatSourceQuestion = () => 'Lead source?' } = helpers;
  const steps = [
    {
      key: 'name',
      ask: 'Contact / applicant name?',
      validate: (v) => (!String(v).trim() || String(v).trim().length < 2 ? 'Enter at least 2 characters.' : null),
      parse: (v) => capitalizeName(v),
      apply: (val) => { data.name = val; },
    },
    {
      key: 'phone',
      ask: 'Mobile number?',
      hint: '+94 77 123 4567 or 0771234567',
      validate: (v) => {
        const phone = helpers.normalizePhone?.(v) || v;
        if (!phone || String(phone).replace(/\D/g, '').length < 9) return 'Enter a valid phone number.';
        return null;
      },
      parse: (v) => (helpers.normalizePhone ? helpers.normalizePhone(v) : v.trim()),
      apply: (val) => { data.phone = val; },
    },
    {
      key: 'package',
      ask: () => {
        const list = (data._packages.length ? data._packages : ['India eVisa', 'Malaysia eVisa', 'Vietnam eVisa', 'Dubai Visa'])
          .slice(0, 8)
          .map((p, i) => `${i + 1}. ${p}`).join('  ');
        return `Visa type / package?\n${list}`;
      },
      validate: (v) => {
        const n = Number(String(v).trim());
        const pkgs = data._packages.length ? data._packages : ['India eVisa', 'Malaysia eVisa', 'Vietnam eVisa', 'Dubai Visa'];
        if (Number.isInteger(n) && n >= 1 && n <= pkgs.length) return null;
        if (String(v).trim().length >= 2) return null;
        return 'Pick a number or type package name.';
      },
      parse: (v) => {
        const pkgs = data._packages.length ? data._packages : ['India eVisa', 'Malaysia eVisa', 'Vietnam eVisa', 'Dubai Visa'];
        const n = Number(String(v).trim());
        if (Number.isInteger(n) && n >= 1 && n <= pkgs.length) return pkgs[n - 1];
        return String(v).trim();
      },
      apply: (val) => { data.package_name = val; },
    },
    {
      key: 'pax_adt',
      ask: 'How many adults (ADT)?',
      validate: (v) => {
        const n = Number(String(v).trim());
        if (!Number.isInteger(n) || n < 1) return 'Enter whole number ≥ 1.';
        return null;
      },
      parse: (v) => Number(String(v).trim()),
      apply: (val) => { data.pax.adt = val; },
    },
    {
      key: 'pax_chd',
      ask: 'Children (CHD)?',
      hint: 'enter to skip',
      optional: true,
      parse: (v) => (isSkip(v) ? 0 : Number(String(v).trim().replace(/,/g, '')) || 0),
      apply: (val) => { data.pax.chd = val; },
    },
    {
      key: 'cost_adt',
      ask: () => {
        const def = data._costs[data.package_name];
        return def
          ? `Cost price per adult (LKR)? Suggested: ${fmtAmt(def)}`
          : 'Cost price per adult (LKR)?';
      },
      validate: (v) => validateLkrAmount(v),
      parse: (v) => Math.round(parseLkrAmount(v)),
      apply: (val) => {
        data.pax_cost_rates = data.pax_cost_rates || { adt: null, chd: null };
        data.pax_cost_rates.adt = val;
      },
    },
    {
      key: 'cost_chd',
      when: () => (data.pax.chd || 0) > 0,
      ask: 'Cost price per child (LKR)?',
      hint: 'Enter to use same as adult',
      optional: true,
      validate: (v) => (isSkip(v) ? null : validateLkrAmount(v)),
      parse: (v) => (isSkip(v) ? null : Math.round(parseLkrAmount(v))),
      apply: (val) => {
        data.pax_cost_rates = data.pax_cost_rates || { adt: null, chd: null };
        data.pax_cost_rates.chd = val ?? data.pax_cost_rates.adt;
      },
    },
    {
      key: 'sell_adt',
      ask: 'Selling price per adult (LKR)?',
      validate: (v) => validateLkrAmount(v),
      parse: (v) => Math.round(parseLkrAmount(v)),
      apply: (val) => { data.pax_rates.adt = val; },
    },
    {
      key: 'sell_chd',
      when: () => (data.pax.chd || 0) > 0,
      ask: 'Selling price per child (LKR)?',
      hint: 'Enter to use same as adult',
      optional: true,
      validate: (v) => (isSkip(v) ? null : validateLkrAmount(v)),
      parse: (v) => (isSkip(v) ? null : Math.round(parseLkrAmount(v))),
      apply: (val) => { data.pax_rates.chd = val ?? data.pax_rates.adt; },
    },
    {
      key: 'india_purpose',
      when: () => isIndiaEvisa(data.package_name),
      ask: 'India eVisa purpose? (1.Tourist, 2.Business, 3.Other)',
      validate: (v) => (/^(1|2|3|tourist|business|other|t|b|o)$/i.test(String(v).trim()) ? null : 'Reply 1, 2, or 3.'),
      parse: (v) => {
        const s = String(v).trim().toLowerCase();
        if (s === '2' || s === 'b' || s === 'business') return 'business';
        if (s === '3' || s === 'o' || s === 'other') return 'other';
        return 'tourist';
      },
      apply: (val) => {
        data.india_evisa = data.india_evisa || {};
        data.india_evisa.purpose = val;
      },
    },
    {
      key: 'india_duration',
      when: () => isIndiaEvisa(data.package_name),
      ask: 'India eVisa duration? (1.30D, 2.1Y)',
      validate: (v) => (/^(1|2|30d|1y|30|1)$/i.test(String(v).trim()) ? null : 'Reply 1 or 2.'),
      parse: (v) => {
        const s = String(v).trim().toLowerCase();
        return (s === '2' || s === '1y' || s === '1') ? '1y' : '30d';
      },
      apply: (val) => {
        data.india_evisa = data.india_evisa || {};
        data.india_evisa.duration = val;
      },
    },
    {
      key: 'passport',
      ask: 'Passport number?',
      hint: 'enter to skip',
      optional: true,
      parse: (v) => (isSkip(v) ? null : upperDoc(v)),
      apply: (val) => { data.passport_no = val; },
    },
    {
      key: 'source',
      ask: formatSourceQuestion,
      validate: (v) => (!matchSource(v) ? 'Pick a source.' : null),
      parse: (v) => matchSource(v),
      apply: (val) => { data.source = val; },
    },
    {
      key: 'note',
      ask: 'Notes?',
      optional: true,
      parse: (v) => (isSkip(v) ? null : v.trim()),
      apply: (val) => { data.note = val; },
    },
  ];
  return steps.filter((s) => !s.when || s.when(data));
}

export function activeVisaSteps(data, helpers) {
  return prepareFlowSteps('visa.new', injectCustomSteps('visa.new', buildVisaSteps(data, helpers), { mode: 'new', data }));
}

export function buildVisaLeadBody(data, agentId, helpers = {}) {
  const adt = data.pax.adt || 1;
  const chd = data.pax.chd || 0;
  const costPerAdt = data.pax_cost_rates?.adt ?? 0;
  const costPerChd = data.pax_cost_rates?.chd ?? costPerAdt;
  const sellPerAdt = data.pax_rates?.adt ?? 0;
  const sellPerChd = data.pax_rates?.chd ?? sellPerAdt;
  const profitPerAdt = sellPerAdt - costPerAdt;
  const profitPerChd = sellPerChd - costPerChd;
  const costTotal = costPerAdt > 0 ? Math.round(costPerAdt * adt + costPerChd * chd) : null;
  const sellTotal = sellPerAdt > 0 ? Math.round(sellPerAdt * adt + sellPerChd * chd) : null;

  const extra = {
    pax: { ...data.pax },
  };
  if (costPerAdt > 0) {
    extra.pax_cost_rates = { adt: costPerAdt, ...(chd > 0 ? { chd: costPerChd } : {}) };
    extra.pax_profit_rates = { adt: profitPerAdt, ...(chd > 0 ? { chd: profitPerChd } : {}) };
  }
  if (sellPerAdt > 0) {
    extra.pax_rates = { adt: sellPerAdt, chd: chd > 0 ? sellPerChd : null, inf: null };
  }
  if (data.passport_no) extra.passport_no = data.passport_no;
  if (data.india_evisa) extra.india_evisa = data.india_evisa;
  mergeCustomFieldsIntoExtra(extra, data._customFields);

  return {
    type: 'visa',
    name: data.name,
    phone: data.phone,
    package_name: data.package_name,
    source: data.source || 'Call',
    status: 'new',
    agent_id: agentId,
    inquiry_date: todayISO(),
    followup_date: helpers.defaultFollowupISO?.() || todayISO(),
    amount: sellTotal,
    cost_amount: costTotal,
    note_text: data.note,
    extra,
  };
}

export function formatVisaSummary(data) {
  const lines = [
    `Visa · ${data.package_name || '—'}`,
    `${data.name} · ${data.phone}`,
    `Pax: ${data.pax.adt} ADT · ${data.pax.chd} CHD`,
  ];
  if (data.pax_cost_rates?.adt != null) {
    lines.push(`Cost: ${fmtAmt(data.pax_cost_rates.adt)}/ADT`);
  }
  if (data.pax_rates?.adt != null) {
    lines.push(`Sell: ${fmtAmt(data.pax_rates.adt)}/ADT`);
    if ((data.pax.chd || 0) > 0 && data.pax_rates.chd != null) {
      lines.push(`Sell CHD: ${fmtAmt(data.pax_rates.chd)}/CHD`);
    }
    if (data.pax_cost_rates?.adt != null) {
      const sellChd = data.pax_rates.chd ?? data.pax_rates.adt;
      const costChd = data.pax_cost_rates.chd ?? data.pax_cost_rates.adt;
      const profitAdt = data.pax_rates.adt - data.pax_cost_rates.adt;
      const profitChd = sellChd - costChd;
      const totalQuote = (data.pax_rates.adt * (data.pax.adt || 1)) + (sellChd * (data.pax.chd || 0));
      lines.push(`Profit: ${fmtAmt(profitAdt)}/ADT${(data.pax.chd || 0) > 0 ? ` · ${fmtAmt(profitChd)}/CHD` : ''} · Total quote: ${fmtAmt(totalQuote)}`);
    }
  }
  if (data.india_evisa?.purpose) {
    const purpose = data.india_evisa.purpose === 'business' ? 'Business' : data.india_evisa.purpose === 'other' ? 'Other' : 'Tourist';
    lines.push(`India eVisa: ${purpose}${data.india_evisa.duration ? ` · ${data.india_evisa.duration}` : ''}`);
  }
  return lines.join('\n');
}

export { isYes, isNo, isSkip, parseSmartDate, validateSmartDate, DATE_HINT };
